import { useEffect, useRef } from 'react'
import { Map, Marker, Polygon, useMap } from '@vis.gl/react-google-maps'
import { AreaMapOverlay } from './area-map-overlay'
import { addPoint, movePoint, normalizeGeo } from './use-area-polygon'
import type { AreaGeo, LatLng } from './use-area-polygon'
import type { AreaMapItem } from '@/lib/api/grower-area-map-api'
import {
    GOOGLE_MAPS_MAP_ID,
    hasGoogleMapsKey,
} from '@/components/maps/google-maps-provider'
import { MapPlaceSearch } from '@/components/maps/place-search/map-place-search'
import { m } from '@/lib/paraglide/messages'

/**
 * Khi vùng đã có toạ độ, lần đầu mở bản đồ zoom/fit thẳng vào vùng đó
 * (chạy một lần cho mỗi lần mount — mỗi tab vùng remount nên tự fit).
 */
function FitBounds({
    paths,
    fitKey,
}: {
    paths: LatLng[]
    fitKey?: string | number
}) {
    const map = useMap()
    // Refit khi đổi vùng (fitKey đổi), KHÔNG refit khi đang vẽ/sửa điểm cùng vùng.
    const lastKey = useRef<string | number | undefined>(undefined)
    useEffect(() => {
        if (!map || paths.length === 0) return
        if (lastKey.current === fitKey) return
        lastKey.current = fitKey
        if (paths.length === 1) {
            map.setCenter(paths[0])
            map.setZoom(17)
            return
        }
        const lats = paths.map((p) => p.lat)
        const lngs = paths.map((p) => p.lng)
        map.fitBounds(
            {
                north: Math.max(...lats),
                south: Math.min(...lats),
                east: Math.max(...lngs),
                west: Math.min(...lngs),
            },
            48,
        )
    }, [map, paths, fitKey])
    return null
}

export const DEFAULT_CENTER: LatLng = { lat: 21.54119, lng: 105.77732 }

/**
 * Lớp nền "thửa xung quanh" (mọi hộ, lazy-load theo ô như màn bản đồ chung) để
 * người vẽ nhìn hàng xóm mà định vị thửa của mình. Không bắt click: click bản
 * đồ vẫn là thêm mốc ranh giới.
 */
export type NeighborContext = {
    /** Hộ đang sửa: thửa DB của hộ này bị ẩn, thay bằng `siblings` từ form. */
    growerId?: string
    /** Thửa khác của hộ đang sửa lấy từ form (kể cả vùng vừa thêm chưa lưu). */
    siblings?: AreaMapItem[]
    /** Thửa đang vẽ — không vẽ đè bằng lớp nền. */
    excludeAreaIds?: string[]
}

type Props = {
    value: { paths?: unknown[]; markers?: unknown[] }
    onChange: (geo: AreaGeo) => void
    readOnly?: boolean
    height?: number
    // Đổi giá trị này để bản đồ fit lại (vd khi chuyển sang vùng khác).
    fitKey?: string | number
    /** `false` = tắt lớp nền thửa xung quanh. Mặc định bật. */
    neighbors?: NeighborContext | false
}

const INITIAL_ZOOM = 15

/**
 * Bản đồ Google (ảnh vệ tinh) để định vị thửa: click thêm đỉnh, kéo marker để
 * chỉnh. `paths`/`markers` giữ đúng cấu trúc dữ liệu cũ để tương thích.
 */
export function GrowerAreaMap({
    value,
    onChange,
    readOnly,
    height = 380,
    fitKey,
    neighbors = {},
}: Props) {
    const geo = normalizeGeo(value.paths, value.markers)
    const center = geo.paths[0] ?? DEFAULT_CENTER

    if (!hasGoogleMapsKey()) {
        return (
            <div
                className="flex items-center justify-center rounded-md border bg-muted text-sm text-muted-foreground"
                style={{ height }}
            >
                {m.grower_map_no_key()}
            </div>
        )
    }

    return (
        <div
            className="relative overflow-hidden rounded-md border"
            style={{ height }}
        >
            <Map
                // Map ID thật (không phải chuỗi tuỳ ý) vì nhãn hàng xóm dùng
                // AdvancedMarker — Google đòi Map ID mới render được.
                mapId={GOOGLE_MAPS_MAP_ID}
                defaultCenter={center}
                defaultZoom={INITIAL_ZOOM}
                mapTypeId="hybrid"
                gestureHandling="greedy"
                // Chỉ xem: bỏ toggle bản đồ/vệ tinh, fullscreen, street view —
                // trên khung 320px của điện thoại chúng che gần hết thửa. Giữ
                // nút zoom vì hộ dân vẫn cần phóng to xem ranh giới.
                disableDefaultUI={readOnly}
                zoomControl={readOnly ? true : undefined}
                style={{ width: '100%', height: '100%' }}
                onClick={(e: {
                    detail?: { latLng?: { lat: number; lng: number } | null }
                }) => {
                    if (readOnly) return
                    const ll = e.detail?.latLng
                    if (!ll) return
                    onChange(addPoint(geo, { lat: ll.lat, lng: ll.lng }))
                }}
            >
                <FitBounds paths={geo.paths} fitKey={fitKey} />
                {!readOnly && <MapPlaceSearch entry="area_editor" />}
                {neighbors !== false && (
                    <AreaMapOverlay
                        interactive={false}
                        initialZoom={INITIAL_ZOOM}
                        excludeGrowerId={neighbors.growerId}
                        excludeAreaIds={neighbors.excludeAreaIds}
                        siblings={neighbors.siblings}
                    />
                )}
                {geo.paths.length > 2 && (
                    <Polygon
                        paths={geo.paths}
                        fillColor="#ef4444"
                        fillOpacity={0.3}
                        strokeColor="#ef4444"
                        strokeWeight={2}
                    />
                )}
                {/* Chỉ xem thì bỏ ghim từng đỉnh: không kéo được, mà 5-7 ghim
                    đỏ phủ kín thửa khiến không còn nhìn ra ranh giới. */}
                {!readOnly &&
                    geo.paths.map((p, i) => (
                        <Marker
                            key={i}
                            position={p}
                            draggable={!readOnly}
                            onDragEnd={(e: {
                                latLng?: {
                                    lat: () => number
                                    lng: () => number
                                } | null
                            }) => {
                                if (readOnly) return
                                const ll = e.latLng
                                if (!ll) return
                                onChange(
                                    movePoint(geo, i, {
                                        lat: ll.lat(),
                                        lng: ll.lng(),
                                    }),
                                )
                            }}
                        />
                    ))}
            </Map>
            {!readOnly && (
                <div className="absolute left-2 top-2 rounded-md bg-black/70 px-2.5 py-1.5 text-xs text-white">
                    {m.grower_map_hint()}
                </div>
            )}
        </div>
    )
}
